import { Directive, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { DEFAULT_INACTIVITY_TIMEOUT, EAutosaveFormEvent } from './autosave-form.enum';
import { ComponentPrototype, isArray } from '../../shared';
import { BehaviorSubject, Subscription, timer } from 'rxjs';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';
import { AnimationFrameService } from '../../services/animation-frame.service';

const { FOCUS, MOUSE_DOWN, KEY_DOWN } = EAutosaveFormEvent;
const DEFAULT_EVENTS = [FOCUS, MOUSE_DOWN, KEY_DOWN];

@Directive({
  selector: '[appAutosaveForm]'
})
export class AutosaveFormDirective extends ComponentPrototype implements OnInit, OnDestroy {
  @Input('appAutosaveForm') public events: any = DEFAULT_EVENTS;

  @Input() public inactivityDelay: number = DEFAULT_INACTIVITY_TIMEOUT;

  @Input() public isActive: boolean = false;

  @Output() public userInactive: EventEmitter<void> = new EventEmitter<void>();

  private isActive$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private core$: Subscription;

  private getEventsList = (): EAutosaveFormEvent[] => {
    const events: EAutosaveFormEvent[] = [];

    this.events &&
      (isArray(this.events) ? this.events : `${this.events}`.split(/,|;/)).forEach(s => {
        const event = s.trim().toLowerCase();
        Object.values(EAutosaveFormEvent).includes(event) && events.push(event);
      });

    return events.length ? events : DEFAULT_EVENTS;
  };

  constructor(private frameService: AnimationFrameService) {
    super();
  }

  ngOnInit(): void {
    const { userInactive, frameService } = this;
    const eventsList = this.getEventsList();

    const resetInactivityTimer = (): void => {
      this.core$?.unsubscribe();
      this.core$ = timer(this.inactivityDelay)
        .pipe(
          tap(() => {
            if (this.isActive) {
              userInactive.emit();
              resetInactivityTimer();
            }
          })
        )
        .subscribe();
    };

    const addListeners = (): void => {
      eventsList.forEach(e => document.addEventListener(e, resetInactivityTimer));
      resetInactivityTimer();
    };

    const removeListeners = (): void => eventsList.forEach(e => document.removeEventListener(e, resetInactivityTimer));

    this.registerSubscriptions(
      frameService.on$
        .pipe(
          map(() => this.isActive),
          distinctUntilChanged(),
          tap(isActive => (isActive ? addListeners() : removeListeners()))
        )
        .subscribe()
    );
  }

  ngOnDestroy(): void {
    this.isActive$.next(false);
    this.core$?.unsubscribe();
    super.ngOnDestroy();
  }
}
